<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { handleOAuthCallback, authStore, getAndClearPostLoginRedirect } from '../lib/auth';
  import { Spinner } from '../lib/components/ui/index';
  import { Button } from '../lib/components/ui/index';

  let error = '';

  onMount(() => {
    // Silent refresh: running inside a hidden iframe
    if (window.self !== window.top) {
      handleIframeCallback();
      return;
    }

    // Normal login flow
    try {
      const user = handleOAuthCallback();

      if (user) {
        authStore.login(user);
        const redirect = getAndClearPostLoginRedirect();
        // Full-page redirect (not SPA navigate): reboots the app so authStore
        // re-inits from the freshly stored token and the target route mounts
        // deterministically. An in-app navigate() here races svelte-routing's
        // route (re-)registration triggered by the authStore change and can
        // leave the callback spinner wedged. replace() also drops the token-
        // bearing callback URL from history.
        window.location.replace(redirect);
      } else {
        error = $_('pages.login.no_token');
      }
    } catch (e) {
      error = e instanceof Error ? e.message : $_('errors.generic');
    }
  });

  function handleIframeCallback(): void {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const idToken = params.get('id_token');
    const state = params.get('state');
    const errorParam = params.get('error');

    // Validate state has silent-refresh prefix (sessionStorage CSRF check
    // doesn't work cross-iframe, so we rely on the prefix convention)
    const isValidState = state?.startsWith('silent-refresh-');

    if (errorParam || !isValidState || !idToken) {
      // Refresh failed (e.g. login_required, interaction_required)
      window.parent.postMessage(
        { type: 'silent-refresh-response', token: null, state },
        window.location.origin,
      );
      return;
    }

    window.parent.postMessage(
      { type: 'silent-refresh-response', token: idToken, state },
      window.location.origin,
    );
  }
</script>

<div class="min-h-screen flex items-center justify-center bg-bg">
  {#if error}
    <div class="bg-surface border border-border rounded-lg p-8 max-w-sm w-[90%] text-center">
      <h2 class="text-lg font-semibold text-text mb-2">{$_('pages.login.auth_failed')}</h2>
      <p class="text-sm text-text-muted mb-6">{error}</p>
      <Button variant="primary" on:click={() => navigate('/login')}>
        {$_('pages.login.back_to_login')}
      </Button>
    </div>
  {:else}
    <div class="text-center">
      <Spinner size="lg" label={$_('pages.login.loading')} />
    </div>
  {/if}
</div>
